// Predefined theme palettes for white labeling
export interface ThemePalette {
  id: string;
  name: string;
  description: string;
  colors: {
    light: {
      primary: string;
      secondary: string;
      background: string;
      surface: string;
      error: string;
      paper: string;
      text: string;
      button?: {
        main: string;
        disabled: string;
      };
    };
    dark: {
      primary: string;
      secondary: string;
      background: string;
      surface: string;
      error: string;
      paper: string;
      text: string;
      button?: {
        main: string;
        disabled: string;
      };
    };
  };
}

export const THEME_PALETTES: ThemePalette[] = [
  {
    id: 'default',
    name: 'Boostt AI v3.0',
    description: 'Boostt AI brand token system v3.0',
    colors: {
      light: {
        primary: '#1B8187',    // --accent-text light
        secondary: '#ECBF2B',  // --accent-yellow
        background: '#F7FAFB', // --bg-app light
        surface: '#EEF3F5',    // --bg-hover light
        error: '#C73A3A',      // --danger-text light
        paper: '#FFFFFF',      // --bg-canvas light
        text: '#0D2430',       // --text-primary light
        button: {
          main: '#1B8187',
          disabled: '#A3B0B6',
        },
      },
      dark: {
        primary: '#1B8187',    // --brand-teal (single interactive color across modes)
        secondary: '#ECBF2B',  // --accent-yellow
        background: '#0A1418', // --bg-app dark
        surface: '#182832',    // --bg-hover dark
        error: '#EF6363',      // --danger-text dark
        paper: '#0F1B22',      // --bg-canvas dark
        text: '#E8EEF0',       // --text-primary dark
        button: {
          main: '#1B8187',
          disabled: '#4C5A61',
        },
      },
    },
  },
  {
    id: 'corporate-blue',
    name: 'Corporate Blue',
    description: 'Professional blue theme for corporate clients',
    colors: {
      light: {
        primary: '#1976d2',
        secondary: '#42a5f5',
        background: '#f5f5f5',
        surface: '#e3f2fd',
        error: '#f44336',
        paper: '#ffffff',
        text: '#212121',
        button: {
          main: '#1976d2',
          disabled: '#90caf9',
        },
      },
      dark: {
        primary: '#90caf9',
        secondary: '#42a5f5',
        background: '#121212',
        surface: '#1e3a8a',
        error: '#ef5350',
        paper: '#1e1e1e',
        text: '#ffffff',
        button: {
          main: '#1976d2',
          disabled: '#90caf9',
        },
      },
    },
  },
  {
    id: 'modern-purple',
    name: 'Modern Purple',
    description: 'Contemporary purple theme for tech companies',
    colors: {
      light: {
        primary: '#8e24aa',
        secondary: '#ba68c8',
        background: '#fafafa',
        surface: '#f3e5f5',
        error: '#e91e63',
        paper: '#ffffff',
        text: '#1a1a1a',
        button: {
          main: '#8e24aa',
          disabled: '#ce93d8',
        },
      },
      dark: {
        primary: '#ce93d8',
        secondary: '#ba68c8',
        background: '#0d0d0d',
        surface: '#4a148c',
        error: '#f48fb1',
        paper: '#1a1a1a',
        text: '#e0e0e0',
        button: {
          main: '#8e24aa',
          disabled: '#ce93d8',
        },
      },
    },
  },
  {
    id: 'green-nature',
    name: 'Green Nature',
    description: 'Eco-friendly green theme for environmental companies',
    colors: {
      light: {
        primary: '#2e7d32',
        secondary: '#66bb6a',
        background: '#f1f8e9',
        surface: '#e8f5e8',
        error: '#4caf50',
        paper: '#ffffff',
        text: '#1b5e20',
        button: {
          main: '#2e7d32',
          disabled: '#81c784',
        },
      },
      dark: {
        primary: '#81c784',
        secondary: '#66bb6a',
        background: '#0d1b0d',
        surface: '#1b5e20',
        error: '#a5d6a7',
        paper: '#1a2e1a',
        text: '#c8e6c9',
        button: {
          main: '#2e7d32',
          disabled: '#81c784',
        },
      },
    },
  },
  {
    id: 'warm-orange',
    name: 'Warm Orange',
    description: 'Energetic orange theme for creative agencies',
    colors: {
      light: {
        primary: '#f57c00',
        secondary: '#ffb74d',
        background: '#fff3e0',
        surface: '#ffe0b2',
        error: '#ff9800',
        paper: '#ffffff',
        text: '#e65100',
      },
      dark: {
        primary: '#ffb74d',
        secondary: '#ffcc02',
        background: '#1a0d00',
        surface: '#e65100',
        error: '#ffcc02',
        paper: '#2e1500',
        text: '#ffe0b2',
      },
    },
  },
  {
    id: 'elegant-red',
    name: 'Elegant Red',
    description: 'Sophisticated red theme for luxury brands',
    colors: {
      light: {
        primary: '#d32f2f',
        secondary: '#f44336',
        background: '#ffebee',
        surface: '#ffcdd2',
        error: '#f44336',
        paper: '#ffffff',
        text: '#b71c1c',
      },
      dark: {
        primary: '#ef5350',
        secondary: '#f44336',
        background: '#1a0000',
        surface: '#b71c1c',
        error: '#f44336',
        paper: '#2e0000',
        text: '#ffcdd2',
      },
    },
  },
  {
    id: 'minimal-grey',
    name: 'Minimal Grey',
    description: 'Clean grey theme for minimalist brands',
    colors: {
      light: {
        primary: '#424242',
        secondary: '#757575',
        background: '#fafafa',
        surface: '#f5f5f5',
        error: '#616161',
        paper: '#ffffff',
        text: '#212121',
      },
      dark: {
        primary: '#bdbdbd',
        secondary: '#9e9e9e',
        background: '#121212',
        surface: '#2e2e2e',
        error: '#757575',
        paper: '#1e1e1e',
        text: '#e0e0e0',
      },
    },
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    description: 'Calming teal theme for healthcare and wellness',
    colors: {
      light: {
        primary: '#00695c',
        secondary: '#26a69a',
        background: '#e0f2f1',
        surface: '#b2dfdb',
        error: '#00acc1',
        paper: '#ffffff',
        text: '#004d40',
      },
      dark: {
        primary: '#4db6ac',
        secondary: '#26a69a',
        background: '#001a17',
        surface: '#004d40',
        error: '#26a69a',
        paper: '#002a26',
        text: '#b2dfdb',
      },
    },
  },
];

export const getThemePalette = (id: string): ThemePalette | undefined => {
  return THEME_PALETTES.find(palette => palette.id === id);
};

export const getDefaultThemePalette = (): ThemePalette => {
  return THEME_PALETTES[0]; // Default palette
};
